import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { checkMeiyeAmount } from '$/api/server';
import Button from '$/components/base/Button';
import { message } from '$/components/Message';
import { replaceTemplateWithElements, useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { openVipModal } from '$/store/useVipModalStore';
import { FileTypeEnum } from '$/utils/upload';
import { LoadingType } from '@meitu/whee-infinite-canvas';
import useAIToolsCanvasStore from '@/store/useAIToolsCanvasStore';
import useAIEnhanceStore, {
  setMode,
  setSingleEraserLoading,
  setImageHdType,
  setSingleResultImage,
  setSingleResultTaskId,
  updateBatchImage,
  setBatchEraserLoading,
  ImageHdType,
  VideoSRMode,
  setVideoSRMode,
} from '@/store/useAIEnhanceStore';
import {
  type AiToolSubmitParamWithCategory,
  type AiToolTaskResultItem,
} from '@/domains/tools/types';
import { countAiToolCost } from '@/domains/tools/toolsService';
import { getMagicSRToolName } from '@/domains/tools/aiToolAlgorithmUri';
import { useShallow } from 'zustand/react/shallow';
import { useCommonStore } from '@/store/useCommonStore';
import { v4 as uuid } from 'uuid';
import radishIcon from '@/assets/radish.png';
import ModeTab from '../../../ai_eraser/components/ModeTab';
import SelectionBox from '../../../ai_eraser/components/SelectionBox';
import PanelWrapper from '../../../ai_eraser/components/PanelWrapper';
import {
  getAiToolTaskResult,
  type GetAiToolTaskResultOptions,
} from '../../../ai_eraser/aiToolsTask';
import {
  AssetToolType,
  autoSaveAiToolGeneratedAsset,
  type AiToolAssetMaterialType,
} from '../../../ai_eraser/utils/assetLibrary';
import { buildAIEnhanceTaskPayload } from '../../utils/buildMagicSRParams';
import {
  buildAiToolVipModalTrackParams,
  type AiToolUploadFunc,
  joinAiToolTrackApiNames,
  trackAiToolRunClick,
  trackAiToolRunSuccess,
} from '../../../utils/aiToolUploadTrack';
import { getBatchEnhanceTrackParams, getEnhanceTrackParams } from '../../utils/enhanceTrack';
import style from './index.module.scss';

/** 基础高清等级选项（高清 / 超清 / AI超清） */
const BASE_HD_TYPE_VALUES = [
  ImageHdType.HD,
  ImageHdType.Super,
  ImageHdType.AISuper,
] as const;

/** 智能识别场景选项 */
const SCENES_HD_TYPE_VALUES = [
  ImageHdType.Goods,
  ImageHdType.Text,
  ImageHdType.OldPicture,
  ImageHdType.Portrait,
  ImageHdType.PortraitPro,
] as const;

/** 基础高清等级图标 */
const BASE_HD_TYPE_ICONS: Record<(typeof BASE_HD_TYPE_VALUES)[number], string> = {
  [ImageHdType.HD]: 'hdRectangleBold',
  [ImageHdType.Super]: 'hdUltraRectangleBold',
  [ImageHdType.AISuper]: 'AIultraHD',
};

/** 智能识别场景图标 */
const SCENES_HD_TYPE_ICONS: Record<(typeof SCENES_HD_TYPE_VALUES)[number], string> = {
  [ImageHdType.Goods]: 'shoppingBagBold',
  [ImageHdType.Text]: 'typefaceBold',
  [ImageHdType.OldPicture]: 'photoResolution',
  [ImageHdType.Portrait]: 'faceHappyBold',
  [ImageHdType.PortraitPro]: 'faceHappyWithProBold',
};

/** 视频变高清模式选项 */
const VIDEO_SR_MODE_ITEMS: { label: string; value: VideoSRMode; icon: string; }[] = [
  { label: '2K', value: VideoSRMode._2K, icon: 'twoKRectangleBold' },
  { label: '4K', value: VideoSRMode._4K, icon: 'fourKRectangleBold' },
  { label: 'AI 4K+', value: VideoSRMode.AI4KPlus, icon: 'AIultraHD' },
];

/** 视频智能识别场景选项 */
const VIDEO_SCENE_MODE_ITEMS: { labelKey?: 'product_mode' | 'portrait_mode' | 'low_light' | 'de_AI' | 'game' | 'anime'; label?: string; value: VideoSRMode; icon: string; }[] = [
  { labelKey: 'portrait_mode', value: VideoSRMode.Portrait, icon: 'faceHappyBold' },
  { labelKey: 'product_mode', value: VideoSRMode.Goods, icon: 'shoppingBagBold' },
  { labelKey: 'low_light', value: VideoSRMode.LowLight, icon: 'moonStar' },
  { labelKey: 'de_AI', value: VideoSRMode.DeAI, icon: 'magicWandBold' },
  { labelKey: 'game', value: VideoSRMode.Game, icon: 'ganeCenter' },
  { labelKey: 'anime', value: VideoSRMode.Anime, icon: 'cartoonBold' },
];

type EnhanceSubmitParam = AiToolSubmitParamWithCategory<'magicsr' | 'video_hd'>;

function getRoomIdFromUrl() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('room_id')?.trim() || '';
}

/**
 * 预加载图片结果，让画布切换高清结果图时尽量命中浏览器缓存。
 */
function preloadEnhanceResultImage(url: string, mediaType: 'image' | 'video') {
  if (typeof window === 'undefined' || !url || mediaType === 'video') return Promise.resolve();
  return new Promise<void>((resolve) => {
    const img = new Image();
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve();
    }, 8000);
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    img.onload = () => {
      if (typeof img.decode === 'function') {
        img.decode().then(finish).catch(finish);
        return;
      }
      finish();
    };
    img.onerror = finish;
    img.src = url;
  });
}

function getBatchItemMediaType(item: { type?: FileTypeEnum; url: string; }) {
  if (item.type === FileTypeEnum.Video) return FileTypeEnum.Video;
  if (item.type === FileTypeEnum.Image) return FileTypeEnum.Image;
  return /\.(mp4|mov|m4v)(\?|#|$)/i.test(item.url) ? FileTypeEnum.Video : FileTypeEnum.Image;
}

/** 自动保存增强生成结果，图片与视频分别写入对应的单功能 tool_type。 */
function autoSaveEnhanceGeneratedAsset(options: {
  duration?: number;
  height?: number;
  mediaType: FileTypeEnum | 'image' | 'video';
  taskId?: string;
  thumbnailUrl?: string;
  url: string;
  width?: number;
}) {
  const isVideo = options.mediaType === FileTypeEnum.Video || options.mediaType === 'video';
  const materialType: AiToolAssetMaterialType = isVideo ? 'video' : 'image';
  autoSaveAiToolGeneratedAsset({
    toolType: isVideo ? AssetToolType.VIDEO_HD : AssetToolType.IMAGE_UPSCALER,
    sourceScene: 'task_generate',
    materialType,
    url: options.url,
    taskId: options.taskId,
    width: options.width,
    height: options.height,
    duration: options.duration,
    thumbnailUrl: options.thumbnailUrl,
  });
}

/** 判断图片高清模式是否需要会员解锁。 */
function isVipOnlyHdType(type: ImageHdType) {
  return type === ImageHdType.Super || type === ImageHdType.AISuper;
}

/** 判断视频高清模式是否需要会员解锁。 */
function isVipOnlyVideoSRMode(mode?: VideoSRMode) {
  return mode === VideoSRMode._4K || mode === VideoSRMode.AI4KPlus;
}

/**
 * 图片高清档位卡片触发会员弹窗时，按具体档位上报 func。
 */
function getVipModalFuncByImageHdType(type: ImageHdType): AiToolUploadFunc {
  if (type === ImageHdType.Super) return 'change_super_hd';
  if (type === ImageHdType.AISuper) return 'change_ai_hd';
  return 'change_to_hd';
}

/**
 * 构建 AI 变高清计费项；新计费协议要求 params 内携带 task_category。
 */
function buildCostItems(paramsMap: Record<string, EnhanceSubmitParam>) {
  return Object.entries(paramsMap).map(([id, params]) => ({
    id,
    tool_name: getMagicSRToolName(params),
    size: '',
    quality: '',
    resolution: '',
    params,
  }));
}

/**
 * 查询 AI 变高清消耗，美叶数量由服务端按每个 item 的 task_category 计算。
 */
async function fetchAIEnhanceCost(paramsMap: Record<string, EnhanceSubmitParam>) {
  const result = await countAiToolCost({
    room_id: getRoomIdFromUrl(),
    items: buildCostItems(paramsMap),
  });
  return (result.items || []).reduce((sum, item) => sum + Number(item.cost || 0), 0);
}

/**
 * 提取本次 AI 高清 countcost 使用的 tool_name，作为订阅弹窗埋点 api_name。
 */
function getEnhanceCostApiName(paramsMap: Record<string, EnhanceSubmitParam>) {
  return joinAiToolTrackApiNames(
    buildCostItems(paramsMap).map((item) => item.tool_name),
  );
}

/**
 * 构建带 task_category 的 AI 变高清参数，供 submit 与 countcost 共用。
 */
function buildEnhanceSubmitParam(options: {
  imageFile: string;
  imageHdType?: ImageHdType;
  videoSRMode?: VideoSRMode;
  mediaType?: FileTypeEnum | 'image' | 'video';
  videoDuration?: number;
}): EnhanceSubmitParam {
  const taskPayload = buildAIEnhanceTaskPayload(options);
  if (taskPayload.taskCategory === 'video_hd') {
    return {
      task_category: 'video_hd',
      ...taskPayload.params,
    };
  }

  return {
    task_category: 'magicsr',
    ...taskPayload.params,
  };
}

/**
 * 构建单个素材的 AI 变高清计费参数。
 */
function buildSingleEnhanceCostParams(options: {
  imageFile: string;
  imageHdType: ImageHdType;
  videoSRMode?: VideoSRMode;
  mediaType: FileTypeEnum | 'image' | 'video';
  videoDuration?: number;
}) {
  return {
    [uuid()]: buildEnhanceSubmitParam(options),
  };
}

/**
 * 构建批量素材的 AI 变高清计费参数，可混合图片 magicsr 与视频 video_hd。
 */
function buildBatchEnhanceCostParams(
  list: Array<{ id: string; url: string; type?: FileTypeEnum; requestDuration?: number; }>,
  imageHdType: ImageHdType,
  videoSRMode?: VideoSRMode,
) {
  return list.reduce<Record<string, EnhanceSubmitParam>>(
    (acc, item) => ({
      ...acc,
      [item.id]: buildEnhanceSubmitParam({
        imageFile: item.url,
        imageHdType,
        videoSRMode,
        mediaType: getBatchItemMediaType(item),
        videoDuration: item.requestDuration,
      }),
    }),
    {},
  );
}

type EnhanceSubmitPayload = { params: Record<string, EnhanceSubmitParam>; };

/**
 * 提交 AI 变高清任务；新协议允许单次请求混合 magicsr 与 video_hd item。
 */
function runEnhanceSubmitPayload(
  payload: EnhanceSubmitPayload,
  onTaskUpdate?: GetAiToolTaskResultOptions<'magicsr' | 'video_hd'>['onTaskUpdate'],
  onTaskCreated?: GetAiToolTaskResultOptions<'magicsr' | 'video_hd'>['onTaskCreated'],
) {
  return getAiToolTaskResult({
    ...payload,
    onTaskUpdate,
    onTaskCreated,
  });
}

/**
 * 构建单个素材的 AI 变高清提交 payload。
 */
function buildSingleEnhanceSubmitPayload(options: {
  id: string;
  imageFile: string;
  imageHdType: ImageHdType;
  videoSRMode?: VideoSRMode;
  mediaType: FileTypeEnum | 'image' | 'video';
  videoDuration?: number;
}): EnhanceSubmitPayload {
  const { id, ...taskOptions } = options;
  return {
    params: { [id]: buildEnhanceSubmitParam(taskOptions) },
  };
}

/**
 * 构建批量素材的 AI 变高清提交 payload；不再按 task_category 拆分请求。
 */
function buildBatchEnhanceSubmitPayload(
  list: Array<{ id: string; url: string; type?: FileTypeEnum; requestDuration?: number; }>,
  imageHdType: ImageHdType,
  videoSRMode?: VideoSRMode,
): EnhanceSubmitPayload {
  return {
    params: list.reduce<Record<string, EnhanceSubmitParam>>((acc, item) => ({
      ...acc,
      [item.id]: buildEnhanceSubmitParam({
        imageFile: item.url,
        imageHdType,
        videoSRMode,
        mediaType: getBatchItemMediaType(item),
        videoDuration: item.requestDuration,
      }),
    }), {}),
  };
}

function getBatchCountLabel(list: Array<{ type?: FileTypeEnum; url: string; }>) {
  // const hasVideo = list.some((item) => getBatchItemMediaType(item) === FileTypeEnum.Video);
  // const hasImage = list.some((item) => getBatchItemMediaType(item) === FileTypeEnum.Image);
  // if (hasImage && hasVideo) return `${list.length}个素材`;
  // if (hasVideo) return `${list.length}个`;
  return `${list.length}`;
}

interface EnhanceLeftPanelProps {
  uploading?: boolean;
}

export default function EnhanceLeftPanel({ uploading = false }: EnhanceLeftPanelProps) {
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const toLogin = useOfficialStore((state) => state.toLogin);
  const isVip = useOfficialStore((state) => Boolean(state.userInfo.subscribe?.isValid));
  const languageStore = useLanguageStore((state) => state.languageStore);

  const { render, imageTarget } = useAIToolsCanvasStore(useShallow((state) => ({
    render: state.render,
    imageTarget: state.imageTarget,
  })));
  const {
    mode,
    singleEraserLoading,
    singleOriginalImage,
    singleResultImage,
    batchImages,
    batchEraserLoading,
    imageHdType,
    singleMediaType,
    videoSRMode,
    singleVideoDuration,
    singleVideoRequestDuration,
  } = useAIEnhanceStore(
    useShallow((state) => ({
      mode: state.mode,
      singleOriginalImage: state.singleOriginalImage,
      singleResultImage: state.singleResultImage,
      singleEraserLoading: state.singleEraserLoading,
      batchImages: state.batchImages,
      batchEraserLoading: state.batchEraserLoading,
      imageHdType: state.imageHdType,
      singleMediaType: state.singleMediaType,
      videoSRMode: state.videoSRMode,
      singleVideoDuration: state.singleVideoDuration,
      singleVideoRequestDuration: state.singleVideoRequestDuration,
    })),
  );
  const [singleEnhanceCost, setSingleEnhanceCost] = useState<number | null>(null);
  const [batchEnhanceCost, setBatchEnhanceCost] = useState<number | null>(null);

  const batchCostKey = useMemo(
    () => batchImages.map((item) => `${item.id}:${item.url}:${item.type || ''}:${item.duration || ''}:${item.requestDuration || ''}`).join('|'),
    [batchImages],
  );
  const modeTabs = useMemo(
    () => [
      { key: 'single' as const, label: languageStore.smartcatTranslationText((v) => v.single_sheet_mode) },
      { key: 'batch' as const, label: languageStore.smartcatTranslationText((v) => v.batch), showVipBadge: true },
    ],
    [languageStore],
  );
  const singleVipLocked = (
    (singleMediaType === 'image' && isVipOnlyHdType(imageHdType))
    || (singleMediaType === 'video' && isVipOnlyVideoSRMode(videoSRMode))
  ) && !isVip;
  const enhanceFailedText = languageStore.smartcatTranslationText((v) => v['4K_fail']);
  const processingFailedText = languageStore.smartcatTranslationText((v) => v.processing_failed);
  const singleUnlockText = singleMediaType === 'video'
    ? languageStore.smartcatTranslationText((v) => v.unlock_4k)
    : languageStore.smartcatTranslationText((v) => v.become_a_member_to_unlock_hd_quality_);
  const batchModeLocked = !isVip;

  useEffect(() => {
    const imageFile = singleResultImage || singleOriginalImage;
    if (!isUserLoggedIn || !imageFile || singleVipLocked) {
      setSingleEnhanceCost(null);
      return undefined;
    }

    let cancelled = false;
    fetchAIEnhanceCost(buildSingleEnhanceCostParams({
      imageFile,
      imageHdType,
      videoSRMode,
      mediaType: singleMediaType,
      videoDuration: singleVideoRequestDuration,
    }))
      .then((cost) => {
        if (!cancelled) setSingleEnhanceCost(cost);
      })
      .catch(() => {
        if (!cancelled) setSingleEnhanceCost(null);
      });

    return () => {
      cancelled = true;
    };
  }, [imageHdType, isUserLoggedIn, singleMediaType, singleOriginalImage, singleResultImage, singleVideoRequestDuration, singleVipLocked, videoSRMode]);

  useEffect(() => {
    if (!isUserLoggedIn || !isVip || !batchImages.length) {
      setBatchEnhanceCost(null);
      return undefined;
    }

    const list = useAIEnhanceStore.getState().batchImages;
    let cancelled = false;
    fetchAIEnhanceCost(buildBatchEnhanceCostParams(list, imageHdType, videoSRMode))
      .then((cost) => {
        if (!cancelled) setBatchEnhanceCost(cost);
      })
      .catch(() => {
        if (!cancelled) setBatchEnhanceCost(null);
      });

    return () => {
      cancelled = true;
    };
  }, [batchCostKey, batchImages.length, imageHdType, isUserLoggedIn, isVip, videoSRMode]);

  const baseHdTypeItems = useMemo(() => [
    {
      value: ImageHdType.HD,
      label: languageStore?.smartcatTranslationText((v) => v.HD),
      icon: BASE_HD_TYPE_ICONS[ImageHdType.HD],
    },
    {
      value: ImageHdType.Super,
      label: languageStore?.smartcatTranslationText((v) => v.uhd),
      icon: BASE_HD_TYPE_ICONS[ImageHdType.Super],
    },
    {
      value: ImageHdType.AISuper,
      label: languageStore?.smartcatTranslationText((v) => v.ai_ultra_definition),
      icon: BASE_HD_TYPE_ICONS[ImageHdType.AISuper],
    },
  ], [languageStore]);

  const scenesHdTypeItems = useMemo(() => [
    {
      value: ImageHdType.Goods,
      label: languageStore?.smartcatTranslationText((v) => v.product_mode),
      icon: SCENES_HD_TYPE_ICONS[ImageHdType.Goods],
    },
    {
      value: ImageHdType.Text,
      label: languageStore?.smartcatTranslationText((v) => v.text_mode),
      icon: SCENES_HD_TYPE_ICONS[ImageHdType.Text],
    },
    {
      value: ImageHdType.OldPicture,
      label: languageStore?.smartcatTranslationText((v) => v.old_picture),
      icon: SCENES_HD_TYPE_ICONS[ImageHdType.OldPicture],
    },
    {
      value: ImageHdType.Portrait,
      label: languageStore?.smartcatTranslationText((v) => v.portrait_mode),
      icon: SCENES_HD_TYPE_ICONS[ImageHdType.Portrait],
    },
    {
      value: ImageHdType.PortraitPro,
      label: languageStore?.smartcatTranslationText((v) => v.portrait_pro),
      icon: SCENES_HD_TYPE_ICONS[ImageHdType.PortraitPro],
    },
  ], [languageStore]);

  const videoSceneModeItems = useMemo(() => VIDEO_SCENE_MODE_ITEMS.map((item) => ({
    value: item.value,
    label: item.labelKey ? languageStore?.smartcatTranslationText((v) => v[item.labelKey!]) : item.label,
    icon: item.icon,
  })), [languageStore]);

  const isBaseHdTypeActive = BASE_HD_TYPE_VALUES.includes(imageHdType as (typeof BASE_HD_TYPE_VALUES)[number]);
  const isScenesHdTypeActive = SCENES_HD_TYPE_VALUES.includes(imageHdType as (typeof SCENES_HD_TYPE_VALUES)[number]);

  /**
   * 校验 AI 变高清余额，不足时带单工具来源参数打开订阅弹窗。
   */
  const ensureRadishEnough = async (cost: number, apiName?: string) => {
    const data = await checkMeiyeAmount();
    if (Number(data.amount || 0) < cost) {
      message.error('RoboNeo_Web_studio_changing_toast', 3);
      openVipModal?.('func_token_short', undefined, buildAiToolVipModalTrackParams('change_to_hd', {
        entrance: 'insufficient_balance',
        apiName,
      }));
      return false;
    }
    return true;
  };

  /**
   * 非余额不足场景在功能页唤起订阅弹窗时，统一带执行页来源参数。
   */
  const openVip = (func: AiToolUploadFunc = 'change_to_hd') => {
    openVipModal?.('func_runpage', undefined, buildAiToolVipModalTrackParams(func));
  };

  const renderCost = (cost: number | null) => {
    if (cost === null) return null;
    return (
      <span className={style.button_cost}>
        <img src={radishIcon.src} alt="" className={style.radish_icon} />
        <span>{cost}</span>
      </span>
    );
  };

  const handleImageHdTypeClick = (value: ImageHdType) => {
    if (isVipOnlyHdType(value) && !useOfficialStore.getState().userInfo.subscribe?.isValid) {
      openVip(getVipModalFuncByImageHdType(value));
      return;
    }
    setImageHdType(value);
  };

  const handleVideoSRModeClick = (value: VideoSRMode) => {
    if (isVipOnlyVideoSRMode(value) && !useOfficialStore.getState().userInfo.subscribe?.isValid) {
      openVip();
      return;
    }
    setVideoSRMode(value);
  };

  /**
   * 登录校验后提交变高清任务
   */
  const handleSubmit = async (type: 'single' | 'batch') => {
    if (!isUserLoggedIn) {
      await toLogin().then(() => {
        initUserInfo();
      });
      if (type === 'single') {
        handleStartSingleEnhance();
      } else {
        handleStartBatchEnhance();
      }
      return;
    }
    if (type === 'single') {
      handleStartSingleEnhance();
    } else {
      handleStartBatchEnhance();
    }
  };

  /**
   * 单图模式变高清
   */
  const handleStartSingleEnhance = async () => {
    if (singleEraserLoading) return;
    setSingleEraserLoading(true);
    let keepCanvasLoadingUntilResultSwap = false;

    const reportFailure = (toastContent?: string) => {
      const text = toastContent?.trim();
      message.error(text || enhanceFailedText);
    };

    try {
      const vipOnlySingleMode = (singleMediaType === 'image' && isVipOnlyHdType(imageHdType))
        || (singleMediaType === 'video' && isVipOnlyVideoSRMode(videoSRMode));
      if (vipOnlySingleMode && !useOfficialStore.getState().userInfo.subscribe?.isValid) {
        openVip();
        return;
      }

      const id = uuid();
      const imageFile = singleResultImage || singleOriginalImage;
      if (!imageFile) {
        message.error(enhanceFailedText);
        return;
      }
      const costParams = buildSingleEnhanceCostParams({
        imageFile,
        imageHdType,
        videoSRMode,
        mediaType: singleMediaType,
        videoDuration: singleVideoRequestDuration,
      });
      const latestCost = await fetchAIEnhanceCost(costParams);
      if (singleEnhanceCost !== null && latestCost !== singleEnhanceCost) {
        message.error(enhanceFailedText);
        return;
      }
      const radishEnough = await ensureRadishEnough(latestCost, getEnhanceCostApiName(costParams));
      if (!radishEnough) return;

      render?.Actions.setLoading({
        id: imageTarget?._id_ || '',
        type: LoadingType.CIRCLE_DANCE,
        text: ' ',
        blur: true,
        maskOpacity: 0.2,
      });

      const payload = buildSingleEnhanceSubmitPayload({
        id,
        imageFile,
        imageHdType,
        videoSRMode,
        mediaType: singleMediaType,
        videoDuration: singleVideoRequestDuration,
      });
      const result = await runEnhanceSubmitPayload(payload, undefined, (ids) => trackAiToolRunClick({
        func: 'change_to_hd',
        taskIds: Object.values(ids),
        generateType: 1,
        ...getEnhanceTrackParams({
          mediaType: singleMediaType,
          imageHdType,
          videoSRMode,
        }),
      }));

      if (result.success) {
        const out = result.data.result?.[id][0];
        const url = out?.url_sign || out?.url_short || '';
        if (url) {
          trackAiToolRunSuccess({
            func: 'change_to_hd',
            taskId: result.ids?.[id],
            resultUrl: url,
            contentType: singleMediaType === 'video' ? 'video' : 'image',
            generateType: 1,
            ...getEnhanceTrackParams({
              mediaType: singleMediaType,
              imageHdType,
              videoSRMode,
            }),
          });
          autoSaveEnhanceGeneratedAsset({
            mediaType: singleMediaType,
            url,
            taskId: result.ids?.[id],
            width: out?.width,
            height: out?.height,
            duration: out?.duration || singleVideoDuration,
            thumbnailUrl: out?.url_thumb_sign,
          });
        }
        preloadEnhanceResultImage(url, singleMediaType).catch(() => {});
        keepCanvasLoadingUntilResultSwap = Boolean(url) && url !== imageFile;
        setSingleResultImage(url);
        setSingleResultTaskId(result.ids?.[id] || '');
        checkMeiyeAmount().catch(() => {});
      } else {
        reportFailure(result.message || undefined);
      }
    } catch (error) {
      console.error('error', error);
      reportFailure();
    } finally {
      if (!keepCanvasLoadingUntilResultSwap) {
        render?.Actions.setLoaded(imageTarget?._id_ || '');
        setSingleEraserLoading(false);
      }
    }
  };

  /**
   * 批量模式变高清
   */
  const handleStartBatchEnhance = async () => {
    if (batchEraserLoading) return;
    if (!useOfficialStore.getState().userInfo.subscribe?.isValid) {
      openVip();
      return;
    }
    if (!batchImages.length) return;

    const list = useAIEnhanceStore.getState().batchImages;
    try {
      const costParams = buildBatchEnhanceCostParams(list, imageHdType, videoSRMode);
      const latestCost = await fetchAIEnhanceCost(costParams);
      if (batchEnhanceCost !== null && latestCost !== batchEnhanceCost) {
        message.error(enhanceFailedText);
        return;
      }
      const radishEnough = await ensureRadishEnough(latestCost, getEnhanceCostApiName(costParams));
      if (!radishEnough) return;
    } catch (error) {
      console.error('批量变高清价格校验失败:', error);
      message.error(enhanceFailedText);
      return;
    }

    list.forEach((item) => updateBatchImage(item.id, { status: 'processing', progress: 0 }));
    setBatchEraserLoading(true);

    const onTaskUpdate = (frontendId: string, item: AiToolTaskResultItem) => {
      if (item.status === 100) {
        const out = item.result_images?.[0];
        const url = out?.url_sign || out?.url_short || '';
        const batchItem = list.find((i) => i.id === frontendId);
        const mediaType = batchItem ? getBatchItemMediaType(batchItem) : FileTypeEnum.Image;
        if (url) {
          trackAiToolRunSuccess({
            func: 'change_to_hd',
            taskId: item.id,
            resultUrl: url,
            contentType: mediaType === FileTypeEnum.Video ? 'video' : 'image',
            generateType: 2,
            ...getEnhanceTrackParams({
              mediaType,
              imageHdType,
              videoSRMode,
            }),
          });
          autoSaveEnhanceGeneratedAsset({
            mediaType,
            url,
            taskId: item.id,
            width: out?.width,
            height: out?.height,
            duration: out?.duration || batchItem?.duration,
            thumbnailUrl: out?.url_thumb_sign,
          });
        }
        updateBatchImage(frontendId, {
          status: 'success',
          taskId: item.id,
          resultUrl: url,
          resultImageLoading: true,
          width: out?.width,
          height: out?.height,
        });
      } else if (item.status >= 90 && item.status < 100) {
        updateBatchImage(frontendId, {
          status: 'failed',
          taskId: item.id,
          errorText: item.fail_msg || processingFailedText,
        });
      }
    };

    try {
      const payload = buildBatchEnhanceSubmitPayload(list, imageHdType, videoSRMode);
      const result = await runEnhanceSubmitPayload(payload, onTaskUpdate, (ids) => trackAiToolRunClick({
        func: 'change_to_hd',
        taskIds: Object.values(ids),
        generateType: 2,
        ...getBatchEnhanceTrackParams({
          items: list,
          imageHdType,
          videoSRMode,
        }),
      }));
      if (!result.success) {
        const failMsg = result.message || enhanceFailedText;
        message.error(enhanceFailedText);
        const current = useAIEnhanceStore.getState().batchImages;
        const hasAnyTerminal = current.some(
          (i) => i.status === 'success' || i.status === 'failed' || i.status === 'unrecognized',
        );
        if (!hasAnyTerminal) {
          list.forEach((item) => updateBatchImage(item.id, { status: 'failed', errorText: failMsg }));
        }
      } else {
        checkMeiyeAmount().catch(() => {});
      }
    } catch (error) {
      console.error('批量变高清失败:', error);
      message.error(enhanceFailedText);
      const listCurrent = useAIEnhanceStore.getState().batchImages;
      listCurrent.forEach((item) => updateBatchImage(item.id, { status: 'failed', errorText: '请求异常' }));
    } finally {
      setBatchEraserLoading(false);
    }
  };

  const singleBtnDisabled = uploading || singleEraserLoading || !singleOriginalImage;
  const isEnhanceProcessing = singleEraserLoading || batchEraserLoading;
  const hasVideoInBatch = batchImages.some((item) => item.type === FileTypeEnum.Video);
  const hasImageInBatch = batchImages.some((item) => item.type === FileTypeEnum.Image);
  const renderSingleButtonContent = () => {
    if (singleEraserLoading) {
      return `${languageStore?.smartcatTranslationText((v) => v.processing)}...`;
    }
    if (singleVipLocked) return singleUnlockText;
    return (
      <span className={style.button_content}>
        <span>{languageStore?.smartcatTranslationText((v) => v.enhance_title)}</span>
        {renderCost(singleEnhanceCost)}
      </span>
    );
  };
  const renderBatchButtonContent = () => {
    if (batchEraserLoading) {
      return `${languageStore?.smartcatTranslationText((v) => v.processing)}...`;
    }
    if (batchModeLocked) {
      return languageStore?.smartcatTranslationText((v) => v.subscribe_to_unlock_batch_high_definition_mode);
    }
    return (
      <span className={style.button_content}>
        <span>{replaceTemplateWithElements('batch_title_n', { title: languageStore?.smartcatTranslationText((v) => v.enhance_title), N: getBatchCountLabel(batchImages) })}</span>
        {renderCost(batchEnhanceCost)}
      </span>
    );
  };

  return createPortal(
    <div className={style.left_panel}>
      {/* 模式切换tab：变高清处理中禁止切换单张/批量 */}
      <ModeTab
        activeMode={mode}
        tabs={modeTabs}
        onChange={setMode}
        disabled={isEnhanceProcessing}
      />

      <div className={style.inner_scroll_box}>
        {/* 视频变高清 */}
        {((mode === 'single' && singleMediaType === 'video') || (mode === 'batch' && hasVideoInBatch)) && (
          <>
            <PanelWrapper
              title={languageStore?.smartcatTranslationText((v) => v.ai_enhance_video)}
              contentClassName={style.panel_wrapper_content}
              hideDivider
            >
              {VIDEO_SR_MODE_ITEMS.map((item) => (
                <SelectionBox
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  icon={item.icon}
                  active={videoSRMode === item.value}
                  disabled={isEnhanceProcessing}
                  vipBadge={isVipOnlyVideoSRMode(item.value)}
                  onClick={() => handleVideoSRModeClick(item.value)}
                />
              ))}
            </PanelWrapper>
            <PanelWrapper
              title={languageStore?.smartcatTranslationText((v) => v.automatic_recognition)}
              subtitle={languageStore?.smartcatTranslationText((v) => v.enhance_based_on_use_case)}
              contentClassName={style.panel_wrapper_content}
              hideDivider
            >
              {videoSceneModeItems.map((item) => (
                <SelectionBox
                  key={item.value}
                  value={item.value}
                  label={item.label || ''}
                  icon={item.icon}
                  active={videoSRMode === item.value}
                  disabled={isEnhanceProcessing}
                  onClick={() => handleVideoSRModeClick(item.value)}
                />
              ))}
            </PanelWrapper>
          </>
        )}
        {/* 图片变高清 */}
        {((mode === 'single' && singleMediaType === 'image') || (mode === 'batch' && (hasImageInBatch || batchImages.length === 0))) && (
          <>
            <PanelWrapper
              title={languageStore?.smartcatTranslationText((v) => v.ai_enhance_image)}
              contentClassName={style.panel_wrapper_content}
            >
              {baseHdTypeItems.map((item) => (
                <SelectionBox
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  icon={item.icon}
                  active={isBaseHdTypeActive && imageHdType === item.value}
                  disabled={isEnhanceProcessing}
                  vipBadge={isVipOnlyHdType(item.value)}
                  onClick={() => handleImageHdTypeClick(item.value)}
                />
              ))}
            </PanelWrapper>
            <PanelWrapper
              title={languageStore?.smartcatTranslationText((v) => v.automatic_recognition)}
              subtitle={languageStore?.smartcatTranslationText((v) => v.enhance_based_on_use_case)}
              contentClassName={style.panel_wrapper_content}
              hideDivider
            >
              {scenesHdTypeItems.map((item) => (
                <SelectionBox
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  icon={item.icon}
                  active={isScenesHdTypeActive && imageHdType === item.value}
                  disabled={isEnhanceProcessing}
                  onClick={() => setImageHdType(item.value)}
                />
              ))}
            </PanelWrapper>
          </>
        )}
      </div>

      {mode === 'single' ? (
        <Button
          type="primary"
          disabled={singleVipLocked ? singleEraserLoading : singleBtnDisabled}
          className={classNames(style.start_eraser_btn, {
            [style.unlock_batch_btn]: singleVipLocked,
          })}
          onClick={() => handleSubmit('single')}
        >
          {renderSingleButtonContent()}
        </Button>
      ) : (
        <Button
          type="primary"
          disabled={batchModeLocked ? (uploading || batchEraserLoading) : (
            uploading || batchEraserLoading || !batchImages.length
          )}
          className={classNames(style.start_eraser_btn, {
            [style.unlock_batch_btn]: batchModeLocked,
          })}
          onClick={() => handleSubmit('batch')}
        >
          {renderBatchButtonContent()}
        </Button>
      )}
    </div>,
    document.body,
  );
}
